import { Marque } from '@/components/brand/Marque'
import { NAV, NAV_ATELIER, NAV_EDITORIAL } from './navigation'

interface Props {
  active: string
  displayName: string
  onNavigate: (id: string) => void
  /** 02 §F7.1 — l'entrée du back-office, pour les seuls rôles éditoriaux. */
  editorial?: boolean
  /**
   * L'espace où l'on se trouve — RG-D9bis, [DECISIONS §115].
   *
   * **Deux métiers, deux navigations.** Ce ne sont pas deux niveaux d'une même
   * arborescence : un candidat révise, un éditeur produit. Leurs écrans n'ont
   * ni les mêmes objets, ni les mêmes gestes, ni la même fréquence.
   */
  espace?: 'candidat' | 'editorial'
}

/**
 * Rail de navigation — 06 §5, disposition Twitter.
 *
 * Trois états, exactement comme Twitter :
 *   < 688 px    absent, la barre du bas prend le relais ;
 *   ≥ 688 px    icônes seules, colonne de 72 px ;
 *   ≥ 1265 px   icônes ET libellés, colonne de 275 px.
 *
 * Les libellés existent TOUJOURS dans le DOM, seulement masqués visuellement
 * en dessous de 1265 px : une icône seule sans nom accessible est un bouton
 * muet pour un lecteur d'écran.
 */
export function NavRail({
  active,
  displayName,
  onNavigate,
  editorial = false,
  espace = 'candidat',
}: Props) {
  // **Le pied du rail n'est pas répété dans la liste** — [DECISIONS §173]. Le
  // bouton du bas mène déjà au compte — avec l'avatar et le nom en plus —, et
  // dans l'atelier il mène déjà au front-office. Les garder dans la liste
  // donnait deux chemins vers le même écran dans la même colonne, à trente
  // centimètres l'un de l'autre. La barre du bas, elle, les conserve : sur
  // mobile il n'y a pas de pied de rail, et l'entrée y est le seul chemin.
  const pied = espace === 'editorial' ? 'front-office' : 'account'

  const entrees = (
    espace === 'editorial' ? NAV_ATELIER : editorial ? [...NAV, NAV_EDITORIAL] : NAV
  ).filter((entry) => entry.id !== pied)

  return (
    <header className="sticky top-0 hidden h-dvh shrink-0 flex-col justify-between px-2 py-1 min-[688px]:flex min-[1265px]:w-[275px]">
      {/* **La liste DÉFILE, et le pied du rail reste ancré** — §173, RG-D9ter.
          Le rail est haut d'un écran exactement ; sans `overflow-y-auto`, CSS
          coupait en silence tout ce qui dépassait — le back-office et le bouton
          de compte, sur un portable ordinaire. `min-h-0` est indispensable :
          sans lui, un enfant flexible refuse de rétrécir sous la hauteur de son
          contenu, et le débordement repart exactement comme avant. */}
      <div className="flex min-h-0 flex-1 flex-col items-start overflow-y-auto [scrollbar-width:thin]">
        {/* La marque SEULE, et accentuée — 06 §8. Le logotype complet serait
            un pâté à 28 px, et la teinte la distingue des icônes de
            navigation juste en dessous, qui portent l'encre du texte. */}
        <span className="flex size-[52px] items-center justify-center">
          <Marque taille={28} ton="couleur" />
        </span>

        {/* **« Navigation principale » était porté par DEUX repères** — le rail et
            la barre du bas —, et un lecteur d'écran qui énumère les repères en
            annonçait deux du même nom sans pouvoir les distinguer. Chacun dit
            maintenant où il se trouve. */}
        <nav aria-label="Navigation latérale" className="flex flex-col items-start">
          {entrees.map((entry) => {
            const courant = entry.id === active

            return (
              <button
                key={entry.id}
                type="button"
                aria-current={courant ? 'page' : undefined}
                onClick={() => { onNavigate(entry.id); }}
                className="my-0.5 flex items-center gap-5 rounded-full px-3 py-3 text-[20px] transition-colors hover:bg-[var(--surface-2)]"
                style={{ color: 'var(--texte-1)' }}
              >
                {entry.icon(courant)}
                {/* Un SEUL libellé, masqué visuellement en rail étroit mais
                    jamais retiré : une icône sans nom accessible est un
                    bouton muet. Deux spans — un visible, un `sr-only` —
                    finiraient par diverger. */}
                <span
                  className={`sr-only pr-4 min-[1265px]:not-sr-only ${courant ? 'font-bold' : ''}`}
                >
                  {entry.label}
                </span>
              </button>
            )
          })}
        </nav>

        {/* **Le bouton « Réviser » a été retiré** (8 août 2026), du rail comme
            de partout ailleurs. Il menait au Fil, qui a déjà son entrée en
            PREMIÈRE position juste au-dessus et en première place de la barre
            du bas. Un troisième chemin vers le même écran occupait la place la
            plus visible du rail sans rien ajouter. */}
      </div>

      {/* Le compte reste au pied du rail côté candidat. Dans l'atelier, il
          n'est pas un poste de travail : on y accède en revenant au
          front-office, comme tout le reste de l'application. */}
      <button
        type="button"
        // Il porte la position courante depuis qu'il est le SEUL chemin du rail
        // vers cet écran (§173). Sans cela, ouvrir son compte n'allumait plus
        // rien dans la colonne : un lecteur d'écran perdait le repère de page.
        aria-current={active === pied ? 'page' : undefined}
        onClick={() => { onNavigate(pied); }}
        className="mt-1 mb-3 flex shrink-0 items-center gap-3 rounded-full p-3 transition-colors hover:bg-[var(--surface-2)]"
      >
        <span
          className="flex size-10 shrink-0 items-center justify-center rounded-full text-[15px] font-bold"
          style={{ background: 'var(--accent-fort)', color: 'var(--sur-accent)' }}
          aria-hidden="true"
        >
          {displayName.slice(0, 1).toUpperCase()}
        </span>
        <span className="sr-only flex-col items-start min-[1265px]:not-sr-only min-[1265px]:flex">
          <span className="text-[15px] font-bold">{displayName}</span>
          <span className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
            {espace === 'editorial' ? 'Revenir à l’application' : 'Voir mon compte'}
          </span>
        </span>
      </button>
    </header>
  )
}
